import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminClamping } from "../../../../../http/patrol/types/patrolClampings";
import { useClampingsApi } from "../../../../../http/patrol/usePatrolClampings";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import ClampingForm from "./ClampingForm";
import CreateClampingModalDialogButton from "./CreateClampingModalDialogButton";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const Clampings: FC<Props> = ({ active }) => {
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "clampings" });
  const aborter = useRef(new AbortController());
  const patrolClampingsApi = useClampingsApi();
  const { setActions, setTitle } = useSearchContext();

  const [itemToEdit, setItemToEdit] = useState<AdminClamping>();
  const [itemToDelete, setItemToDelete] = useState<AdminClamping>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "clampings");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = patrolClampingsApi.useAdminClampings(query, aborter.current.signal, patrolSystemId);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.clampings.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.clampings.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateClampingModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminClamping>
        isTall
        filters={filters}
        head={
          <tr>
            {!patrolSystemId && <th align="left">{i18n.translation.patrolSystems.singular}</th>}
            <th align="left">{i18n.translation.common.topClamp}</th>
            <th align="left">{i18n.translation.common.bottomItems}</th>
            <th align="left">{i18n.translation.roofSubstructures.singular}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => {
          const topItems = [item.topItem?.code, item.topExtraItem?.code].filter(Boolean);

          return (
            <>
              {!patrolSystemId && <td>{item.system.name}</td>}
              <td>
                {!!item.includeSelectedComponent && <i>{i18n.translation.common.selectedComponent}</i>}
                {!!item.includeSelectedComponent && !!topItems.length && " + "}
                {topItems.join(" + ")}
              </td>

              <td>{item.bottomItems?.map((i) => i.code).join("; ")}</td>
              <td>{item.roofSubstructure?.name}</td>
            </>
          );
        }}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.clampings.edit}>
          <FlexContent direction="row">
            <ClampingForm clamping={itemToEdit} onSave={() => setItemToEdit(undefined)} />
          </FlexContent>
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.clampings.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                patrolClampingsApi
                  .deleteAdminClamping(patrolSystemId!, itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.clampings.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default Clampings;
